// Firebase Storage helpers for audio clips

import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '@/firebase.ts';

const URL_RESOLVE_TIMEOUT_MS = 4000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timerId = window.setTimeout(() => {
      reject(new Error(`Timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise
      .then((value) => resolve(value))
      .catch((error) => reject(error))
      .finally(() => window.clearTimeout(timerId));
  });
}

// Get download URL for an audio clip
export async function getClipUrl(filename: string): Promise<string> {
  // Local bundled clips (from Vite public/) should bypass Firebase Storage lookup.
  if (filename.startsWith('/audio/')) {
    return filename;
  }

  try {
    const clipRef = ref(storage, filename);
    return await withTimeout(getDownloadURL(clipRef), URL_RESOLVE_TIMEOUT_MS);
  } catch (error) {
    console.error(`Failed to get URL for ${filename}:`, error);
    // Fall back to the original path so local/static clips can still resolve.
    return filename;
  }
}

// Preload all clip URLs
export async function preloadClipUrls(filenames: string[]): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();
  
  await Promise.all(
    filenames.map(async (filename) => {
      try {
        const url = await getClipUrl(filename);
        urlMap.set(filename, url);
      } catch (error) {
        console.error(`Failed to preload ${filename}:`, error);
      }
    })
  );
  
  return urlMap;
}
